/* PHALANX search — one calm overlay over the PUBLIC site: products, published
 * articles, the pages reachable from the main navigation, and the questions in
 * the FAQ.
 *
 *  - What is searchable is an ALLOWLIST (SEARCHABLE_CONTENT below). Nothing is
 *    indexed and then filtered away: 404, cart, the search page, policies,
 *    system templates and unpublished/hidden pages are simply not listed, and
 *    Shopify's /search/suggest.json results are checked against the same list.
 *  - Page content is read from the rendered pages themselves (the text inside
 *    each page's [data-ph-search-source] container), once, on first use, and
 *    cached in sessionStorage — one source of truth, no second copy.
 *  - Nothing is fetched until the overlay is opened.
 *
 * Matching is accent- and case-insensitive and tolerates simple Swedish
 * plural/definite forms (kreatin / kreatinet) via a light suffix stemmer; no
 * fuzzy engine. Ranking is simple and deterministic (see rank()).
 */
(function () {
  'use strict';

  var root = document.getElementById('ph-search');
  if (!root) return;

  /* ── SEARCHABLE_CONTENT: the ONE allowlist ───────────────────────────────
     pages.<key>
       url      public route (also the allowlist entry for Shopify page results)
       title    result title        label  small type label under the title
       root     stable container on the rendered page ([data-ph-search-source])
       skip     parts of the root left out of the page text (navigation, footer,
                decorative parts or parts that are indexed as their own items)
       max      cap on indexed page text
       items    optional deep-linkable sub entries inside the page
                  item    selector for one entry     title  heading inside it
                  panelOf attribute pointing at its answer by id (FAQ)
                  group   result group (faq | pages)   anchor  link to its #id
     products / articles: path prefixes accepted from /search/suggest.json.
     A page that cannot be read is skipped; the rest keeps working. In
     development a console.warn names it. Any edit here changes the cache
     fingerprint, so stale caches are never reused.                          */
  var CHROME = '#ph-nav, #ph-mobile-menu, #ph-search, footer, nav, script, style, noscript, svg, template, form, [data-ph-search-skip]';
  var SEARCHABLE_CONTENT = {
    pages: {
      about:   { url: '/pages/about',     title: 'Om oss',         label: 'Sida', root: '[data-ph-search-source="about"]',   skip: CHROME, max: 12000 },
      mission: { url: '/pages/var-mission', title: 'Vår mission', label: 'Sida', root: '[data-ph-search-source="mission"]', skip: CHROME, max: 6000 },
      contact: { url: '/pages/contact',   title: 'Kontakta oss',   label: 'Sida', root: '[data-ph-search-source="contact"]', skip: CHROME, max: 4000 },
      science: { url: '/pages/vetenskap', title: 'Vetenskap',      label: 'Sida', root: '[data-ph-search-source="science"]', skip: CHROME, max: 40000,
                 items: { item: 'article.phsci-ingr', title: '.phsci-ingr__name', group: 'pages', anchor: true, max: 900 } },
      quality: { url: '/pages/kvalitet',  title: 'Kvalitet',       label: 'Sida', root: '[data-ph-search-source="quality"]', skip: CHROME, max: 12000 },
      faq:     { url: '/pages/faq',       title: 'Vanliga frågor', label: 'Sida', root: '[data-ph-search-source="faq"]',     skip: CHROME + ', .ph-faq__list, .phfaq__index', max: 1500,
                 items: { item: '.ph-faq__btn', title: '.ph-faq__q', panelOf: 'aria-controls', group: 'faq', anchor: true, label: 'Vanliga frågor' } },
      gamechanger: { url: '/products/game-changer', title: 'Game Changer', label: 'Kognitionsdryck', asGroup: 'products', root: '[data-ph-search-source="gamechanger"]', skip: CHROME + ', .phd-gallery, .phd-info__cta, .phd-sticky-cta', max: 16000 },
      products: { url: '/collections',   title: 'Produkter',      label: 'Sida', root: '[data-ph-search-source="products"]',   skip: CHROME, max: 3000 },
      blog:    { url: '/blogs/nyheter',   title: 'Blogg',          label: 'Sida', root: '[data-ph-search-source="blog"]',    skip: CHROME + ', .phb-grid', max: 1500 }
    },
    products: ['/products/'],
    articles: ['/blogs/nyheter/']
  };
  var CACHE_VERSION = 7;                       // bump when the entry shape, allowlist or extraction rules change
  var CACHE_TTL = 3600000;                     // 1 h
  var CACHE_KEY = 'ph-search-kb';
  var FAQ_HASH = /^#ph-faq-btn-/;               // deep-link ids produced by the FAQ section (see SEARCHABLE_CONTENT.pages.faq)

  var DEV = /^(localhost|127\.0\.0\.1)$/.test(location.hostname) || (window.Shopify && window.Shopify.designMode === true);
  function warn(msg) { if (DEV && window.console && console.warn) console.warn('[phalanx-search] ' + msg); }

  var input = root.querySelector('.ph-search__input');
  var list = root.querySelector('.ph-search__results');
  var allLink = root.querySelector('.ph-search__all');
  var form = root.querySelector('form');
  var closeBtn = root.querySelector('.ph-search__close');
  var openers = document.querySelectorAll('[data-ph-search-open]');
  var lastOpener = null;
  var knowledge = null;          // Promise<Array<entry>>
  var controller = null;
  var timer = 0;
  var seq = 0;

  var L = {
    hint: 'Sök bland produkter, sidor, artiklar och frågor.',
    none: 'Inga träffar för',
    all: 'Visa alla resultat',
    products: 'Produkter', articles: 'Artiklar', pages: 'Sidor', faq: 'Frågor',
    article: 'Artikel'
  };

  /* ── Text helpers ─────────────────────────────────────────────── */
  function fold(s) {
    return String(s || '').toLowerCase()
      .replace(/å/g, 'a').replace(/ä/g, 'a').replace(/ö/g, 'o')
      .normalize('NFD').replace(/[̀-ͯ]/g, '');
  }
  function words(s) { return fold(s).split(/[^a-z0-9]+/).filter(Boolean); }
  var SUFFIX = ['ernas', 'arnas', 'ornas', 'erna', 'arna', 'orna', 'ens', 'ets', 'ers', 'ars', 'ors', 'er', 'ar', 'or', 'en', 'et', 'as', 'es', 'n', 's', 'a', 'e'];
  function stem(w) {
    for (var i = 0; i < SUFFIX.length; i++) {
      var s = SUFFIX[i];
      if (w.length - s.length >= 3 && w.slice(-s.length) === s) return w.slice(0, -s.length);
    }
    return w;
  }
  function tokenMatch(qs, stems) {
    for (var i = 0; i < stems.length; i++) {
      if (stems[i].indexOf(qs) === 0) return 2;                       // word starts with token
      if (qs.length >= 4 && stems[i].length >= 4 && qs.indexOf(stems[i]) === 0) return 1;   // token is an inflected form of the word
    }
    return 0;
  }
  function clean(html) {
    var d = document.createElement('div');
    d.innerHTML = String(html || '');
    return (d.textContent || '').replace(/\s+/g, ' ').trim();
  }
  function pathOf(u) { return String(u || '').split('#')[0].split('?')[0].replace(/\/+$/, ''); }

  /* Score an entry against the query tokens; 0 = no match (every token must match).
     Title hits weigh 5-6 per token, body hits 1; an exact title adds a large bonus. */
  function score(entry, q) {
    if (!entry._t) {
      entry._t = words(entry.title).map(stem);
      entry._b = words(entry.text).map(stem);
    }
    var total = 0;
    for (var i = 0; i < q.length; i++) {
      var qs = stem(q[i]);
      var a = tokenMatch(qs, entry._t);
      var b = a ? 0 : tokenMatch(qs, entry._b);
      if (!a && !b) return 0;
      total += a ? 5 + a : 1;
    }
    var ft = fold(entry.title), fq = q.join(' ');
    if (words(entry.title).join(' ') === fq) total += 30;       // exact title
    else if (ft.indexOf(fq) === 0) total += 4;
    return total;
  }
  /* Final order: best score first; the group only breaks ties and nudges
     (product name > FAQ question > article > general page), so it stays readable. */
  var GROUP_BONUS = { products: 8, faq: 3, articles: 2, pages: 0 };
  function rank(entry, q) { return entry._s + (GROUP_BONUS[entry.group] || 0); }

  function excerpt(text, q) {
    text = String(text || '');
    if (!text) return '';
    var f = fold(text), at = -1;
    for (var i = 0; i < q.length && at < 0; i++) {
      at = f.search(new RegExp('(^|[^a-z0-9])' + q[i].replace(/[^a-z0-9]/g, '') + ''));
      if (at >= 0 && !/^[a-z0-9]/.test(f.charAt(at))) at++;
      if (at < 0) { var st = stem(q[i]); at = st.length >= 3 ? f.indexOf(st) : -1; }
    }
    if (at < 0) return '';
    var start = at > 36 ? at - 36 : 0;
    if (start > 0) { var sp = text.indexOf(' ', start); if (sp > -1 && sp < at) start = sp + 1; }
    var out = text.slice(start, start + 100).trim();
    return (start > 0 ? '… ' : '') + out + (text.length > start + 100 ? ' …' : '');
  }

  /* ── Allowlist checks ─────────────────────────────────────────── */
  var PAGE_PATHS = (function () {
    var m = {}, p = SEARCHABLE_CONTENT.pages;
    Object.keys(p).forEach(function (k) { m[pathOf(p[k].url)] = 1; });
    return m;
  }());
  function startsWithAny(path, prefixes) { return prefixes.some(function (x) { return path.indexOf(x) === 0 && path.length > x.length; }); }

  /* ── Sources ──────────────────────────────────────────────────── */
  function suggest(q, signal) {
    var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
      '&resources[type]=product,article,page&resources[limit]=5&resources[limit_scope]=each';
    return fetch(url, { signal: signal, headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        var res = (j && j.resources && j.resources.results) || {};
        function map(arr, group, allowed) {
          return (Array.isArray(arr) ? arr : []).filter(function (x) {
            return x && typeof x.title === 'string' && x.title && typeof x.url === 'string' && x.url.charAt(0) === '/' && allowed(pathOf(x.url));
          }).map(function (x) {
            return { group: group, label: group === 'articles' ? L.article : '', title: x.title, url: pathOf(x.url), text: clean(x.body || x.content || x.summary_html || ''), page: pathOf(x.url) };
          });
        }
        return map(res.products, 'products', function (p) { return startsWithAny(p, SEARCHABLE_CONTENT.products); })
          .concat(map(res.articles, 'articles', function (p) { return startsWithAny(p, SEARCHABLE_CONTENT.articles); }),
                  map(res.pages, 'pages', function (p) { return PAGE_PATHS[p] === 1; }));
      })
      .catch(function (e) { if (e && e.name === 'AbortError') throw e; return []; });
  }

  /* Stable fingerprint of the allowlist/extraction config: any edit invalidates old caches. */
  function fingerprint() {
    var str = CACHE_VERSION + '|' + JSON.stringify(SEARCHABLE_CONTENT), h = 5381;
    for (var i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
    return (h >>> 0).toString(36);
  }
  function validEntries(d) {
    if (!Array.isArray(d)) return null;
    var out = [];
    for (var i = 0; i < d.length; i++) {
      var e = d[i];
      if (e && typeof e === 'object' && typeof e.title === 'string' && e.title && typeof e.url === 'string' && e.url.charAt(0) === '/' && typeof e.group === 'string' && /^(pages|faq|products)$/.test(e.group) && PAGE_PATHS[pathOf(e.url)] === 1) {
        out.push({
          group: e.group, label: typeof e.label === 'string' ? e.label : '', title: e.title, url: e.url,
          text: typeof e.text === 'string' ? e.text : '',
          page: pathOf(e.url), pageTitle: typeof e.pageTitle === 'string' ? e.pageTitle : ''
        });
      }
    }
    return out;
  }
  function readCache() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var c = JSON.parse(raw);
      if (!c || c.f !== fingerprint() || typeof c.t !== 'number' || Date.now() - c.t > CACHE_TTL) { sessionStorage.removeItem(CACHE_KEY); return null; }
      var d = validEntries(c.d);
      if (!d || !d.length) { sessionStorage.removeItem(CACHE_KEY); return null; }
      return d;
    } catch (e) { try { sessionStorage.removeItem(CACHE_KEY); } catch (_) {} return null; }
  }
  function writeCache(d) { try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ f: fingerprint(), t: Date.now(), d: d })); } catch (e) {} }

  /* Visible text with a space at every tag boundary, so adjacent blocks never glue together
     ("Citikolin400mg"); entities are decoded by clean(). */
  function textOf(el) {
    if (!el) return '';
    return clean(String(el.innerHTML || '').replace(/<(script|style|template)[\s\S]*?<\/\1>/gi, ' ').replace(/<[^>]+>/g, ' '));
  }

  /* Extract one page from a parsed document. Never throws; returns [] if nothing matches. */
  function extract(name, def, doc) {
    var out = [];
    var rootEl = null;
    try { rootEl = doc.querySelector(def.root); } catch (e) { warn('page "' + name + '": invalid root selector ' + def.root); return out; }
    if (!rootEl) { warn('page "' + name + '": container ' + def.root + ' not found on ' + def.url + ' (markup changed?). Skipped.'); return out; }
    try {
      /* items first (they live inside the root and may be removed from the page text below) */
      if (def.items) {
        var it = def.items, nodes = [];
        try { nodes = rootEl.querySelectorAll(it.item); } catch (e) { warn('page "' + name + '": invalid item selector ' + it.item); }
        if (!nodes.length) warn('page "' + name + '": items "' + it.item + '" match nothing on ' + def.url + ' (markup changed?). Page text still indexed.');
        var seen = {};
        for (var i = 0; i < nodes.length; i++) {
          try {
            var el = nodes[i];
            var title = textOf(el.querySelector(it.title)) || textOf(el.querySelector('h1, h2, h3, h4'));
            if (!title || seen[title]) continue;
            seen[title] = 1;
            var body = '';
            if (it.panelOf && el.getAttribute(it.panelOf)) body = textOf(doc.getElementById(el.getAttribute(it.panelOf)));
            if (!body) body = textOf(el);
            if (it.max) body = body.slice(0, it.max);
            var url = def.url;
            if (it.anchor && el.id && /^[A-Za-z][\w:.-]*$/.test(el.id)) url += '#' + el.id;
            out.push({ group: it.group, label: it.label || title, title: title, url: url, text: body, page: pathOf(def.url), pageTitle: def.title });
          } catch (e) { /* one malformed item never affects the rest */ }
        }
      }
      /* the page itself: visible text of the container minus navigation/footer/etc. */
      var clone = rootEl.cloneNode(true);
      try { Array.prototype.forEach.call(clone.querySelectorAll(def.skip), function (n) { if (n.parentNode) n.parentNode.removeChild(n); }); } catch (e) { warn('page "' + name + '": invalid skip selector'); }
      var text = textOf(clone).slice(0, def.max || 8000);
      if (text) out.push({ group: def.asGroup || 'pages', label: def.label, title: def.title, url: def.url, text: text, page: pathOf(def.url), pageTitle: def.title, isPage: true });
    } catch (e) { warn('page "' + name + '": extraction failed (' + (e && e.message) + '). Skipped.'); }
    return out;
  }

  function loadSource(name) {
    var def = SEARCHABLE_CONTENT.pages[name];
    return fetch(def.url, { headers: { Accept: 'text/html' } })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
      .then(function (h) { return extract(name, def, new DOMParser().parseFromString(h, 'text/html')); })
      .catch(function (e) { warn('page "' + name + '": could not read ' + def.url + ' (' + (e && e.message) + '). Skipped.'); return []; });
  }

  function loadKnowledge() {
    if (knowledge) return knowledge;
    var cached = readCache();
    if (cached) { knowledge = Promise.resolve(cached); return knowledge; }
    knowledge = Promise.all(Object.keys(SEARCHABLE_CONTENT.pages).map(loadSource)).then(function (lists) {
      var all = [];
      lists.forEach(function (l) { all = all.concat(l); });
      if (all.length) writeCache(all);
      return all;
    }).catch(function () { return []; });
    return knowledge;
  }

  /* ── Rendering ────────────────────────────────────────────────── */
  function clear() { while (list.firstChild) list.removeChild(list.firstChild); }
  function note(text) {
    clear();
    var p = document.createElement('p');
    p.className = 'ph-search__note';
    p.textContent = text;
    list.appendChild(p);
  }
  function render(results, q, raw) {
    clear();
    var order = ['products', 'articles', 'pages', 'faq'];
    var any = false;
    order.forEach(function (g) {
      var items = results.filter(function (r) { return r.group === g; }).slice(0, g === 'faq' ? 4 : 3);
      if (!items.length) return;
      any = true;
      var sec = document.createElement('section');
      sec.className = 'ph-search__group';
      var h = document.createElement('h2');
      h.className = 'ph-search__group-title';
      h.textContent = L[g];
      sec.appendChild(h);
      items.forEach(function (r) {
        var a = document.createElement('a');
        a.className = 'ph-search__item';
        a.href = r.url;
        var t = document.createElement('span');
        t.className = 'ph-search__title';
        t.textContent = r.pageTitle && g === 'pages' ? r.pageTitle : r.title;
        a.appendChild(t);
        var label = r.label;
        if (label) {
          var m = document.createElement('span');
          m.className = 'ph-search__meta';
          m.textContent = label;
          a.appendChild(m);
        }
        var ex = (g === 'faq' || g === 'pages' || g === 'articles') ? excerpt(r.text, q) : '';
        if (ex) {
          var e = document.createElement('span');
          e.className = 'ph-search__excerpt';
          e.textContent = ex;
          a.appendChild(e);
        }
        sec.appendChild(a);
      });
      list.appendChild(sec);
    });
    if (!any) note(L.none + ' "' + raw + '".');
    allLink.hidden = false;
    allLink.href = '/search?q=' + encodeURIComponent(raw);
  }

  /* ── Query flow ───────────────────────────────────────────────── */
  function run() {
    var raw = input.value.trim();
    var q = words(raw);
    var my = ++seq;
    if (controller) controller.abort();
    if (raw.length < 2 || !q.length) { allLink.hidden = true; note(L.hint); return; }
    controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    list.setAttribute('aria-busy', 'true');
    Promise.all([suggest(raw, controller && controller.signal), loadKnowledge()]).then(function (res) {
      if (my !== seq) return;
      var merged = [];
      /* Shopify results: already matched by Shopify; keep them even if our token match is 0 */
      res[0].forEach(function (r) { r._s = score(r, q) || 3; merged.push(r); });
      res[1].forEach(function (e) { var s = score(e, q); if (s) { var c = Object.assign({}, e); c._s = s; merged.push(c); } });
      merged.forEach(function (r) { r._r = rank(r, q); });
      merged.sort(function (a, b) { return b._r - a._r || (a.url < b.url ? -1 : a.url > b.url ? 1 : 0); });
      /* one result per page / product / article / question: the best-ranked entry wins */
      var seen = {};
      merged = merged.filter(function (r) {
        var k = r.group + '|' + (r.group === 'faq' ? r.url : (r.page || pathOf(r.url)));
        if (seen[k]) return false;
        seen[k] = 1;
        return true;
      });
      render(merged, q, raw);
      list.removeAttribute('aria-busy');
    }).catch(function (e) { if (!e || e.name !== 'AbortError') { list.removeAttribute('aria-busy'); note(L.none + ' "' + raw + '".'); } });
  }

  input.addEventListener('input', function () {
    clearTimeout(timer);
    timer = setTimeout(run, 130);
  });

  /* ── Open / close ─────────────────────────────────────────────── */
  var focusables = function () { return Array.prototype.slice.call(root.querySelectorAll('input, button, a[href]')).filter(function (e) { return !e.hidden && e.offsetParent !== null; }); };
  function open(from) {
    lastOpener = from || document.activeElement;
    /* Opened from inside the mobile menu or the "Om oss" dropdown: close that first,
       and return focus to the control that owns it (the opener itself is about to be hidden). */
    if (from && from.closest('#ph-mobile-menu')) {
      var burger = document.getElementById('ph-burger');
      if (window.__phNavClose) window.__phNavClose(false, false);
      lastOpener = burger || lastOpener;
    } else if (from && from.closest('[data-ph-dd]')) {
      var trig = document.getElementById('ph-nav-dd-trigger');
      if (trig && trig.getAttribute('aria-expanded') === 'true') trig.click();
      lastOpener = trig || lastOpener;
    }
    root.hidden = false;
    document.documentElement.classList.add('ph-search-open');
    requestAnimationFrame(function () { root.classList.add('is-open'); });
    openers.forEach(function (o) { o.setAttribute('aria-expanded', 'true'); });
    if (!input.value) { allLink.hidden = true; note(L.hint); }
    setTimeout(function () { input.focus(); }, 30);
    loadKnowledge();                       // warm the knowledge index in the background
  }
  function close() {
    if (root.hidden) return;
    root.classList.remove('is-open');
    document.documentElement.classList.remove('ph-search-open');
    openers.forEach(function (o) { o.setAttribute('aria-expanded', 'false'); });
    var done = function () { root.hidden = true; };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) done();
    else setTimeout(done, 220);
    if (lastOpener && lastOpener.focus) lastOpener.focus();
  }
  openers.forEach(function (o) {
    o.addEventListener('click', function (e) { e.preventDefault(); open(o); });
  });
  closeBtn.addEventListener('click', close);
  root.addEventListener('click', function (e) { if (e.target === root || e.target.classList.contains('ph-search__scrim')) close(); });
  list.addEventListener('click', function (e) { if (e.target.closest('a')) close(); });
  allLink.addEventListener('click', close);

  root.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key === 'Tab') {
      var f = focusables();
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      var items = Array.prototype.slice.call(list.querySelectorAll('.ph-search__item'));
      if (!items.length) return;
      e.preventDefault();
      var i = items.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') i = i < items.length - 1 ? i + 1 : items.length - 1;
      else i = i > 0 ? i - 1 : -1;
      if (i === -1) input.focus(); else items[i].focus();
    }
  });
  form.addEventListener('submit', function (e) {
    var v = input.value.trim();
    if (!v) { e.preventDefault(); return; }
    // Enter goes to the full results page; the overlay is for quick answers.
  });

  /* ── Full results page (/search) ─────────────────────────────────
     Shopify's own search only knows products, articles and the (empty) page
     bodies, so the sections of PHALANX pages, the product page text and the
     FAQ never appear there. The same knowledge index the overlay uses is
     appended below Shopify's results as a second block, with the same query,
     scoring and de-duplication: whatever Shopify already returned (by path)
     is not repeated. Markup lives in sections/phalanx-search.liquid. */
  function initFullResults(box) {
    var raw = (new URLSearchParams(location.search).get('q') || '').trim();
    var q = words(raw);
    if (raw.length < 2 || !q.length) return;
    var KICKER = { products: 'Produkt', pages: 'Sida', faq: 'Fråga' };
    loadKnowledge().then(function (all) {
      /* Shopify also returns policy pages, system pages and anything else published;
         the allowlist is the same one the overlay uses, so keep only what it names. */
      var have = {};
      Array.prototype.forEach.call(document.querySelectorAll('.phs-grid > a.phs-card'), function (a) {
        var p = pathOf(a.getAttribute('href')), ok = a.classList.contains('phs-card--product') ? startsWithAny(p, SEARCHABLE_CONTENT.products)
          : a.classList.contains('phs-card--article') ? startsWithAny(p, SEARCHABLE_CONTENT.articles) : PAGE_PATHS[p] === 1;
        if (ok) have[p] = 1; else a.parentNode.removeChild(a);
      });
      var kept = Object.keys(have).length;
      var found = [];
      all.forEach(function (e) {
        var s = score(e, q);
        if (!s) return;
        var c = Object.assign({}, e);
        c._s = s; c._r = rank(c, q);
        found.push(c);
      });
      found.sort(function (a, b) { return b._r - a._r || (a.url < b.url ? -1 : a.url > b.url ? 1 : 0); });
      var seen = {};
      found = found.filter(function (r) {
        var k = r.group + '|' + (r.group === 'faq' ? r.url : (r.page || pathOf(r.url)));
        if (seen[k] || (r.group !== 'faq' && have[r.page || pathOf(r.url)])) return false;
        seen[k] = 1;
        return true;
      }).slice(0, 12);
      var total = kept + found.length;
      var countLine = document.querySelector('.phs-results__count:not(.phs-local__count)');
      if (!total) {
        if (countLine) countLine.hidden = true;
        var serverGrid = document.querySelector('.phs-grid:not(.phs-local__grid)');
        if (serverGrid) serverGrid.hidden = true;
        var late = box.querySelector('.phs-local__empty');
        if (late) { late.hidden = false; box.hidden = false; }
        return;
      }
      if (!found.length) { if (kept !== parseInt((document.querySelector('[data-phs-count]') || {}).textContent, 10) && document.querySelector('[data-phs-count]')) document.querySelector('[data-phs-count]').textContent = String(kept); return; }
      var grid = box.querySelector('.phs-local__grid');
      found.forEach(function (r) {
        var a = document.createElement('a');
        a.className = 'phs-card phs-card--page';
        a.href = r.url;
        var body = document.createElement('div');
        body.className = 'phs-card__body phs-card__body--page';
        var k = document.createElement('span');
        k.className = 'phs-card__kicker';
        k.textContent = KICKER[r.group] || 'Sida';
        var h = document.createElement('h2');
        h.className = 'phs-card__title';
        h.textContent = r.pageTitle && r.group === 'pages' ? r.pageTitle : r.title;
        body.appendChild(k); body.appendChild(h);
        var ex = excerpt(r.text, q);
        if (ex) { var p = document.createElement('p'); p.className = 'phs-card__excerpt'; p.textContent = ex; body.appendChild(p); }
        a.appendChild(body);
        grid.appendChild(a);
      });
      var count = document.querySelector('[data-phs-count]');
      if (count) count.textContent = String(total);
      else {
        /* Shopify found nothing: replace its empty state with this block and its own count line */
        var empty = document.querySelector('.phs-empty');
        if (empty) empty.hidden = true;
        var line = box.querySelector('.phs-local__count');
        if (line) { line.querySelector('[data-phs-local-count]').textContent = String(found.length); line.hidden = false; }
      }
      box.hidden = false;
    });
  }
  var fullBox = document.querySelector('[data-ph-search-local]');
  if (fullBox) initFullResults(fullBox);

  /* Arriving with an FAQ hash from a search result: open that question. */
  function openFaqFromHash() {
    var h = location.hash;
    if (!FAQ_HASH.test(h)) return;
    var b = document.getElementById(h.slice(1));
    if (b && b.getAttribute('aria-expanded') === 'false') b.click();
    if (b && b.scrollIntoView) { var nav = document.getElementById('ph-nav'); var gap = (nav ? nav.getBoundingClientRect().bottom : 0) + 16; var top = b.getBoundingClientRect().top; if (top < gap || top > window.innerHeight - 80) window.scrollTo({ top: window.pageYOffset + top - gap, behavior: 'auto' }); }
  }
  window.addEventListener('load', function () { setTimeout(openFaqFromHash, 350); });
  window.addEventListener('hashchange', openFaqFromHash);
}());
